import type { Resource, ResourceRate } from '__types__';
import { RateInterval } from '__types__/enums';
import type { RateCardOption } from 'v5/core/entity-schema/entitySchema.types';
import type {
  FieldCodec,
  RateFormValue,
  ValidationPart,
  ValidationResult
} from 'v5/core/entity-values/values.types';
import { rateRules } from 'v5/core/rates/rateRules';
import {
  isResourceRateInterval,
  RATE_FEES,
  type RateFeeColumn
} from 'v5/core/rates/resourceRate';
import { validation } from './validation';

/** Rates in display order: `order` ascending with nulls last, then id. */
export function orderedRates(entity: Resource): ResourceRate[] {
  return [...(entity.rates ?? [])].sort((a, b) => {
    const aOrder = a.order ?? Number.POSITIVE_INFINITY;
    const bOrder = b.order ?? Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) return aOrder < bOrder ? -1 : 1;
    return a.id - b.id;
  });
}

function roundOrNull(value: number | null): number | null {
  return value === null ? null : Math.round(value);
}

const RATE_INTERVALS: ReadonlyArray<string> = Object.values(RateInterval);

/** Rate cards store `interval` as a plain string; anything unknown falls back to daily. */
function toRateInterval(interval: string): RateInterval {
  return RATE_INTERVALS.includes(interval)
    ? (interval as RateInterval)
    : RateInterval.Daily;
}

/** A row linked to a rate card (`rate_id`) mirrors the card; its own fields are ignored. */
export function isRateCardRow(value: RateFormValue): boolean {
  return value.rate_id !== null;
}

function isEmpty(value: RateFormValue): boolean {
  if (isRateCardRow(value)) return false;
  return value.name === '' && value.amount === null && value.description === '';
}

/**
 * Form value for the list's "add from rate card" option: `rate_id` set and the
 * card's columns copied so the row reads like the card even
 * before the backend echoes it back.
 */
export function rateCardFormValue(card: RateCardOption): RateFormValue {
  return {
    rate_id: card.id,
    name: card.name,
    description: card.description,
    currency: card.currency,
    amount: card.amount,
    interval: toRateInterval(card.interval),
    agency_fee: card.agencyFee,
    payroll_fee: card.payrollFee,
    processing_fee: card.processingFee
  };
}

const feeParts: { [K in RateFeeColumn]: ValidationPart } = {
  agency_fee: 'agency-fee',
  payroll_fee: 'payroll-fee',
  processing_fee: 'processing-fee'
};

/** An amount `contact_rates` could not store in that currency; none is fine. */
export function rateAmountProblem(
  amount: number | null,
  currency: string
): ValidationResult {
  if (amount === null) return validation.valid();
  const check = rateRules.amount(amount, currency);
  switch (check.type) {
    case 'not-a-number':
      return validation.invalid({ code: 'invalid-amount', at: 'amount' });
    case 'negative':
      return validation.invalid({ code: 'negative-amount', at: 'amount' });
    case 'too-many-decimals':
      return validation.invalid(
        check.maxFractionDigits === 0
          ? { code: 'no-decimals', currency, at: 'amount' }
          : {
              code: 'too-many-decimals',
              max: check.maxFractionDigits,
              at: 'amount'
            }
      );
    case 'too-large':
      return validation.invalid({
        code: 'out-of-range',
        min: 0,
        max: check.max,
        at: 'amount'
      });
    case 'valid':
    case 'missing':
      return validation.valid();
  }
}

/** What `contact_rates` could not store, whether or not the rate is required. */
function storageProblem(value: RateFormValue): ValidationResult {
  if (!isResourceRateInterval(value.interval)) {
    return validation.invalid({ code: 'billing-unit', at: 'interval' });
  }
  const amount = rateAmountProblem(value.amount, value.currency);
  if (amount.type === 'invalid') return amount;
  for (const { column } of RATE_FEES) {
    const fee = rateRules.fee(value[column]);
    if (fee.type === 'not-a-number') {
      return validation.invalid({
        code: 'invalid-fee',
        part: feeParts[column],
        at: column
      });
    }
    if (fee.type === 'not-a-whole-percent') {
      return validation.invalid({
        code: 'fee-whole-percent',
        part: feeParts[column],
        at: column
      });
    }
  }
  return validation.valid();
}

/** `rateCodec.validate` on the value alone, for forms that hold rates of their own. */
export function validateRateValue(value: RateFormValue): ValidationResult {
  if (isRateCardRow(value)) return validation.valid();
  if (value.name.trim() === '') {
    return validation.invalid({
      code: 'part-required',
      part: 'name',
      at: 'name'
    });
  }
  if (value.currency === '') {
    return validation.invalid({ code: 'part-required', part: 'currency' });
  }
  if (value.amount === null) {
    return validation.invalid({
      code: 'part-required',
      part: 'amount',
      at: 'amount'
    });
  }
  return storageProblem(value);
}

/**
 * One rate of the rates list; the list encoder assigns ids.
 *
 * Rows with `rate_id` are rate-card rows: never empty, always valid (the
 * card is the source of truth), and encoded with `rateId` — the body
 * builder nulls their own columns.
 *
 * Every custom rate needs a name, a currency and an amount, whether or not
 * the field is required: the list saves every row it holds. A `null` fee is
 * no fee. `validate` also refuses what `contact_rates` cannot store
 * (fractional or out-of-range fees, negative or too large amounts, more
 * decimals than the currency has, ITEM).
 */
export const rateCodec: FieldCodec<'rate'> = {
  decode(entity, spec, ctx) {
    if (ctx.target.type !== 'rates-item') {
      throw new Error(
        `Rate field "${spec.key}" can only be decoded from a rates item, got target "${ctx.target.type}"`
      );
    }
    const rate = orderedRates(entity)[ctx.target.index];
    if (rate === undefined) {
      throw new Error(
        `Rate field "${spec.key}": no rate at index ${ctx.target.index}`
      );
    }
    return {
      rate_id: rate.rate_id ?? null,
      name: rate.name ?? '',
      description: rate.description ?? '',
      currency: rate.currency ?? spec.defaultCurrency,
      amount: rate.amount ?? null,
      interval: rate.interval ?? RateInterval.Daily,
      agency_fee: rate.agency_fee ?? null,
      payroll_fee: rate.payroll_fee ?? null,
      processing_fee: rate.processing_fee ?? null
    };
  },
  encode(value) {
    return {
      type: 'rate',
      item: {
        id: null,
        rateId: value.rate_id,
        name: value.name,
        description: value.description,
        currency: value.currency,
        amount: value.amount,
        interval: value.interval,
        agencyFee: roundOrNull(value.agency_fee),
        payrollFee: roundOrNull(value.payroll_fee),
        processingFee: roundOrNull(value.processing_fee)
      }
    };
  },
  empty(spec) {
    return {
      rate_id: null,
      name: '',
      description: '',
      currency: spec.defaultCurrency,
      amount: null,
      interval: RateInterval.Daily,
      agency_fee: 0,
      payroll_fee: 0,
      processing_fee: 0
    };
  },
  isEmpty,
  validate: validateRateValue
};
